import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Stack, Typography, ButtonProps } from '@mui/material';
import { ResultStatus } from '@dispatch/shared/types';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  p: 4,
  padding: 0,
  boxShadow: 1,
  borderRadius: 1,
  border: 'none',
};

type ModalType =
  | 'activate'
  | 'deactivate'
  | 'cancelTask'
  | 'delete'
  | 'complete'
  | 'custom'
  | 'bulkCancelTask'
  | 'bulkLockTask'
  | 'export';

export type ConfirmModalProps = {
  onConfirm: () => void;
  onDeny: () => void;
  confirmContent?: string | React.ReactElement;
  confirmTitle?: string | React.ReactElement;
  modalType?: ModalType | null;
  // status of action that is performed when clicking confirm
  actionStatus?: ResultStatus;
  confirmButtonLabel?: string;
  confirmColor?:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'success'
    | 'info'
    | 'warning'
    | 'inherit';
  denyButtonLabel?: string;
};

export function ConfirmModal({
  onConfirm,
  onDeny,
  confirmContent,
  confirmTitle,
  modalType = null,
  actionStatus,
  confirmButtonLabel = 'Discard',
  denyButtonLabel = 'Cancel',
  confirmColor = 'primary',
}: ConfirmModalProps) {
  const defaultButtonProps: Partial<ButtonProps> = {
    variant: 'contained',
    color: 'info',
    sx: {
      fontWeight: '700',
      lineHeight: '1.3750em',
      letterSpacing: '0.029em',
    },
  };

  return (
    <Modal
      open={true}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      data-testid="confirm"
    >
      <Stack sx={style}>
        <Box sx={{ padding: '10px 15px' }}>
          <>
            <Stack direction="row" justifyContent="space-between">
              <Typography
                component="h1"
                sx={{ fontSize: '20px', fontWeight: '700' }}
                data-testid={'confirm-modal-title'}
              >
                {confirmTitle || 'Discard changes?'}
              </Typography>
              <HighlightOffIcon
                sx={{
                  '&:hover': { color: 'red' },
                }}
                onClick={() => onDeny()}
              />
            </Stack>
            <Typography
              component="p"
              sx={{ fontSize: '16px' }}
              data-testid={'confirm-modal-content'}
            >
              {confirmContent ||
                'Are you sure you want to exit? Doing so will discard changes.'}
            </Typography>
          </>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'end', padding: '10px' }}
        >
          {(() => {
            switch (modalType) {
              case 'activate':
                return (
                  <>
                    <Button
                      data-testid="modal-activate-cancel"
                      {...defaultButtonProps}
                      onClick={onDeny}
                      variant="text"
                    >
                      Cancel
                    </Button>
                    <Button
                      data-testid="modal-activate-confirm"
                      {...defaultButtonProps}
                      onClick={onConfirm}
                      color="success"
                    >
                      Activate
                    </Button>
                  </>
                );
              case 'deactivate':
                return (
                  <>
                    <Button
                      data-testid="modal-deactivate-cancel"
                      {...defaultButtonProps}
                      onClick={onDeny}
                      variant="text"
                    >
                      Cancel
                    </Button>
                    <Button
                      data-testid="modal-deactivate-confirm"
                      {...defaultButtonProps}
                      onClick={onConfirm}
                      color="error"
                    >
                      Deactivate
                    </Button>
                  </>
                );
              case 'cancelTask':
                return (
                  <>
                    <Button
                      {...defaultButtonProps}
                      onClick={onDeny}
                      variant="text"
                      data-testid="deny-cancel-task"
                      disabled={
                        actionStatus ? actionStatus === 'loading' : undefined
                      }
                    >
                      No
                    </Button>
                    <Button
                      {...defaultButtonProps}
                      onClick={onConfirm}
                      color="error"
                      data-testid="confirm-cancel-task"
                      disabled={
                        actionStatus ? actionStatus === 'loading' : undefined
                      }
                    >
                      {actionStatus && actionStatus === 'loading'
                        ? 'Cancelling'
                        : 'Yes'}
                    </Button>
                  </>
                );
              case 'bulkCancelTask':
                return (
                  <>
                    <Button
                      {...defaultButtonProps}
                      onClick={onDeny}
                      variant="text"
                      disabled={actionStatus === 'loading'}
                    >
                      No
                    </Button>
                    <Button
                      {...defaultButtonProps}
                      onClick={onConfirm}
                      color="error"
                      disabled={actionStatus === 'loading'}
                    >
                      {actionStatus === 'loading' ? 'Cancelling' : 'Yes'}
                    </Button>
                  </>
                );
              case 'bulkLockTask':
                return (
                  <>
                    <Button
                      {...defaultButtonProps}
                      onClick={onDeny}
                      variant="text"
                      data-testid="deny-bulk-lock-task"
                      disabled={actionStatus === 'loading'}
                    >
                      No
                    </Button>
                    <Button
                      {...defaultButtonProps}
                      onClick={onConfirm}
                      color="warning"
                      data-testid="confirm-bulk-lock-task"
                      disabled={actionStatus === 'loading'}
                    >
                      {actionStatus === 'loading' ? 'Locking' : 'Lock Tasks'}
                    </Button>
                  </>
                );
              case 'export':
                return (
                  <>
                    <Button
                      {...defaultButtonProps}
                      onClick={onDeny}
                      variant="text"
                      disabled={actionStatus === 'loading'}
                    >
                      Cancel
                    </Button>
                    <Button
                      {...defaultButtonProps}
                      onClick={onConfirm}
                      color="error"
                      disabled={actionStatus === 'loading'}
                    >
                      {actionStatus === 'loading' ? 'Exporting' : 'Export'}
                    </Button>
                  </>
                );
              case 'delete':
                return (
                  <>
                    <Button
                      {...defaultButtonProps}
                      onClick={onDeny}
                      variant="text"
                    >
                      Cancel
                    </Button>
                    <Button
                      {...defaultButtonProps}
                      onClick={onConfirm}
                      color="error"
                    >
                      Delete
                    </Button>
                  </>
                );
              case 'complete':
                return (
                  <>
                    <Button
                      {...defaultButtonProps}
                      onClick={onDeny}
                      variant="text"
                    >
                      Cancel
                    </Button>
                    <Button
                      {...defaultButtonProps}
                      onClick={onConfirm}
                      color="error"
                    >
                      Complete
                    </Button>
                  </>
                );
              default:
                return (
                  <>
                    <Button
                      {...defaultButtonProps}
                      onClick={onDeny}
                      variant="text"
                    >
                      {denyButtonLabel}
                    </Button>
                    <Button
                      {...defaultButtonProps}
                      onClick={onConfirm}
                      color={confirmColor}
                    >
                      {confirmButtonLabel}
                    </Button>
                  </>
                );
            }
          })()}
        </Stack>
      </Stack>
    </Modal>
  );
}
